import type React from 'react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/** The bar's three buttons. */
type ActionBarAction = 'excludeAll' | 'import' | 'suggestedFixes';

/** Each button's test id, literal so a search finds it. */
const TEST_ID = {
    excludeAll: 'catalog-import-action-bar-exclude-all',
    import: 'catalog-import-action-bar-import',
    suggestedFixes: 'catalog-import-action-bar-suggested-fixes',
} as const satisfies Record<ActionBarAction, string>;

type ActionBarButtonProps = {
    action: ActionBarAction;
    /** The label, already localized by the bar (short on a phone). */
    label: string;
    variant: 'primary' | 'secondary';
    isDisabled: boolean;
    /** A phone: HeroUI's small size. */
    isNarrow: boolean;
    onPress: () => void;
    /** The id of the line that says why the button is disabled, or what it does. */
    describedBy?: string;
};

/**
 * One of the action bar's buttons: on a phone it grows from its own label
 * into the row's free width and wraps to the next line where it does not
 * fit; from sm up it keeps its label's width.
 */
export const ActionBarButton: React.FC<ActionBarButtonProps> = ({
    action,
    label,
    variant,
    isDisabled,
    isNarrow,
    onPress,
    describedBy,
}) => (
    <Button
        aria-describedby={describedBy}
        className="min-w-0 flex-auto sm:flex-none"
        isDisabled={isDisabled}
        onPress={onPress}
        size={isNarrow ? 'sm' : 'md'}
        variant={variant}
        {...getNodeTestIdProp(TEST_ID[action])}
    >
        {label}
    </Button>
);
